import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MenuIcon from '@mui/icons-material/Menu';
import { Box, CSSObject, Checkbox, Divider, Drawer, IconButton, List, ListItem, ListSubheader, Theme, Tooltip, Typography, alpha, styled } from '@mui/material';

import { useIsMobile } from '../../common';
import { Permissions } from '../../common/Permissions';
import { UserClaims } from '../../common/UserClaims';
import { usePersistedSetting } from '../../common/hooks/usePersistedSetting';
import { MenuSectionDesc, menuItems } from '../../containers/Navigation';
import { useUserClaim } from '../../hooks/useUserClaim';
import { appShape } from '../../themes/shapeTokens';
import AuthContext from '../AuthContext';
import { useCurrentUserPermissions } from '../CurrentUserPermissionsContext';
import MenuItemChangeSetSelector from './MenuItemChangeSetSelector';
import MenuListItem from './MenuListItem';
import MenuListItemAvatar from './MenuListItemAvatar';

const DRAWER_WIDTH = 240;
const LEFT_MENU_OPEN_STATE_KEY = 'leftMenu.open';
const LEFT_MENU_HOVER_ENABLED_KEY = 'leftMenu.hoverEnabled';

const getMenuPath = (path: string) => (path.startsWith('/') ? path : `/${path}`);

function normalizePath(path: string) {
	const normalized = path.split(/[?#]/)[0].replace(/\/+$/, '');
	return normalized || '/';
}

function isRouteSelected(currentPath: string, routePath?: string) {
	if (!routePath) {
		return false;
	}

	const current = normalizePath(currentPath);
	const route = normalizePath(routePath);

	if (route === '/') {
		return current === '/';
	}

	return current === route || current.startsWith(`${route}/`);
}

const openedMixin = (theme: Theme): CSSObject => ({
	width: DRAWER_WIDTH,
	transition: theme.transitions.create('width', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.enteringScreen,
	}),
	overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
	transition: theme.transitions.create('width', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen,
	}),
	overflowX: 'hidden',
	width: `calc(${theme.spacing(7)} + 1px)`,
	[theme.breakpoints.up('sm')]: {
		width: `calc(${theme.spacing(8)} + 1px)`,
	},
});

const DrawerHeader = styled('div')(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	padding: theme.spacing(0, 1),
	borderBottom: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
	...theme.mixins.toolbar,
}));

const StyledDrawer = styled(Drawer, {
	shouldForwardProp: (prop) => prop !== 'open' && prop !== 'isOverlay',
})<{ open?: boolean; isOverlay?: boolean }>(({ theme }) => {
	const drawerBackgroundColor = theme.palette.navigation.background;
	const drawerEdgeGap = theme.layout.edgeGap;
	const closedDrawerPaperWidth = `calc(${theme.spacing(7)} + 1px)`;
	const closedDrawerPaperWidthUpSm = `calc(${theme.spacing(8)} + 1px)`;
	const drawerShadow = `0 4px 16px ${alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.2 : 0.07)}`;

	return {
		width: DRAWER_WIDTH + drawerEdgeGap,
		flexShrink: 0,
		whiteSpace: 'nowrap',
		boxSizing: 'border-box',
		'& .MuiDrawer-paper': {
			boxShadow: drawerShadow,
			backgroundColor: drawerBackgroundColor,
			backgroundImage: 'none',
			opacity: 1,
			borderRight: '1px solid',
			borderColor: theme.palette.divider,
			left: drawerEdgeGap,
			top: drawerEdgeGap,
			height: `calc(100% - ${drawerEdgeGap * 2}px)`,
			borderRadius: appShape.surfaceRadius,
			overflow: 'hidden',
		},
		variants: [
			{
				props: ({ open, isOverlay }: { open?: boolean; isOverlay?: boolean }) => open && !isOverlay,
				style: {
					...openedMixin(theme),
					width: DRAWER_WIDTH + drawerEdgeGap,
					'& .MuiDrawer-paper': {
						...openedMixin(theme),
						boxShadow: drawerShadow,
						backgroundColor: drawerBackgroundColor,
						borderRight: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : undefined,
						width: DRAWER_WIDTH,
					},
				},
			},
			{
				props: ({ open, isOverlay }: { open?: boolean; isOverlay?: boolean }) => !open || isOverlay,
				style: {
					...closedMixin(theme),
					width: `calc(${theme.spacing(7)} + 1px + ${drawerEdgeGap}px)`,
					[theme.breakpoints.up('sm')]: {
						width: `calc(${theme.spacing(8)} + 1px + ${drawerEdgeGap}px)`,
					},
					'& .MuiDrawer-paper': {
						...closedMixin(theme),
						boxShadow: drawerShadow,
						backgroundColor: drawerBackgroundColor,
						borderRight: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : undefined,
						width: closedDrawerPaperWidth,
						[theme.breakpoints.up('sm')]: {
							width: closedDrawerPaperWidthUpSm,
						},
					},
				},
			},
			{
				props: ({ isOverlay }: { isOverlay?: boolean }) => isOverlay,
				style: {
					'& .MuiDrawer-paper': {
						position: 'fixed',
						zIndex: theme.zIndex.drawer + 1,
						width: DRAWER_WIDTH,
					},
				},
			},
		],
	};
});

interface ExtendedLeftMenuProps {
	onToggle?: () => void;
	onOpenArchitect?: () => void;
}

export default function LeftMenu({ onToggle, onOpenArchitect }: ExtendedLeftMenuProps) {
	const [open, setOpen] = usePersistedSetting<boolean>(LEFT_MENU_OPEN_STATE_KEY, false);
	const [hoverEnabled, setHoverEnabled] = usePersistedSetting<boolean>(LEFT_MENU_HOVER_ENABLED_KEY, true);
	const [isHovering, setIsHovering] = React.useState(false);
	const [isMobileOpen, setIsMobileOpen] = React.useState(false);
	const auth = useContext(AuthContext);
	const { hasPermission, isLoading: permissionsLoading } = useCurrentUserPermissions();
	const location = useLocation();
	const isMobile = useIsMobile();

	// Get claim values for required claims
	const canChangeTenantId = useUserClaim(UserClaims.CanChangeTenantId);

	const isDrawerOpen = isMobile ? isMobileOpen : open || (hoverEnabled && isHovering);
	const isOverlayMode = isMobile ? isMobileOpen : !open && hoverEnabled && isHovering;

	useEffect(() => {
		setIsMobileOpen(false);
	}, [location.pathname]);

	const handleMouseEnter = () => {
		if (!isMobile && hoverEnabled) setIsHovering(true);
	};

	const handleMouseLeave = () => {
		setIsHovering(false);
	};

	const handleHoverToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
		setHoverEnabled(event.target.checked);
	};

	const handleDrawerToggle = () => {
		if (isMobile) {
			setIsMobileOpen((current) => !current);
		} else {
			setOpen(!open);
		}
		if (onToggle) {
			onToggle();
		}
	};

	// Helper function to check if user has required claim
	const hasRequiredClaim = useCallback(
		(claimName: string | undefined): boolean => {
			if (!claimName) return true;
			// Map claim names to their values
			if (claimName === UserClaims.CanChangeTenantId) return canChangeTenantId;
			// Add more claims here as needed
			return false;
		},
		[canChangeTenantId],
	);

	const sections = useMemo<MenuSectionDesc[]>(
		() =>
			menuItems
				.map((section) => {
					const items = section.items.filter((item) => {
						// Check auth requirement
						if (item.requiredAuth !== undefined && item.requiredAuth !== auth.isAuthenticated) {
							return false;
						}
						if (item.hiddenWhenAuthenticated && auth.isAuthenticated) {
							return false;
						}
						// Check claim requirement
						if (item.requiredClaim && !hasRequiredClaim(item.requiredClaim)) {
							return false;
						}
						if (item.requiredPermission && (permissionsLoading || !hasPermission(item.requiredPermission))) {
							return false;
						}
						if (item.requiredPermissions?.some((permissionId) => permissionsLoading || !hasPermission(permissionId))) {
							return false;
						}
						if (item.requiredAnyPermissions && (permissionsLoading || !item.requiredAnyPermissions.some(hasPermission))) {
							return false;
						}
						return true;
					});
					return {
						...section,
						items: items,
					};
				})
				.filter((section) => section.items.length > 0),
		[auth.isAuthenticated, hasPermission, hasRequiredClaim, permissionsLoading],
	);

	const menuContent = (menuOpen: boolean, showHoverToggle: boolean) => (
		<>
			{/* Product mark and drawer toggle */}
			<DrawerHeader
				sx={
					menuOpen
						? {
								position: 'relative',
								overflow: 'hidden',
								'&::before': {
									content: '""',
									position: 'absolute',
									inset: 0,
									backgroundImage: (theme) =>
										theme.palette.mode === 'dark'
											? 'url("/assets/randol-fabric-dark.png")'
											: 'url("/assets/randol-fabric.png")',
									backgroundPosition: 'right center',
									backgroundSize: 'auto 100%',
									backgroundRepeat: 'no-repeat',
									opacity: 0.3,
									pointerEvents: 'none',
								},
								'& > *': { position: 'relative', zIndex: 1 },
							}
						: undefined
				}
			>
				{menuOpen && (
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: 1,
							minWidth: 0,
							pl: 0.75,
							pr: 1,
							py: 0.5,
						}}
					>
						<Box
							role="img"
							aria-label="Randol"
							sx={{
								width: 42,
								height: 42,
								flexShrink: 0,
								backgroundImage: 'url("/assets/octopus.png")',
								backgroundPosition: 'center',
								backgroundSize: 'contain',
								backgroundRepeat: 'no-repeat',
								filter: (theme) => `drop-shadow(0 5px 14px ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.45 : 0.25)})`,
							}}
						/>
						<Typography
							variant="subtitle1"
							sx={{
								color: 'navigation.item',
								fontWeight: 800,
								letterSpacing: '0.12em',
								textTransform: 'lowercase',
							}}
						>
							Randol
						</Typography>
					</Box>
				)}
				<IconButton
					size="small"
					aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
					onClick={handleDrawerToggle}
					sx={{
						color: 'navigation.item',
						flexShrink: 0,
						bgcolor: (theme) => alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.22 : 0.6),
						'&:hover': { bgcolor: (theme) => alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.34 : 0.82) },
						...(menuOpen ? {} : { mx: 'auto' }),
					}}
				>
					{(isMobile ? isMobileOpen : open) ? <ChevronLeftIcon /> : <ChevronRightIcon />}
				</IconButton>
			</DrawerHeader>

			<Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0, overflow: 'hidden' }}>
				<Box
					component="nav"
					aria-label="Main navigation"
					sx={{
						flex: 1,
						minHeight: 0,
						overflowX: 'hidden',
						overflowY: 'auto',
						py: menuOpen ? 0 : 0.5,
					}}
				>
					{/* Menu sections */}
					{sections.map((section, index) => (
						<React.Fragment key={index}>
							{!menuOpen && index > 0 && <Divider sx={{ mx: 1, opacity: 0.5 }} />}
							<List
								dense
								sx={{
									py: section.title ? 0 : menuOpen ? 1 : 1.75,
									px: menuOpen ? 0 : 0.5,
								}}
								subheader={
									menuOpen && section.title ? (
										<ListSubheader
											component="div"
											sx={{
												backgroundColor: 'inherit',
												color: 'navigation.sectionLabel',
												fontSize: '11px',
												fontWeight: 600,
												textTransform: 'uppercase',
												letterSpacing: '0.5px',
												lineHeight: '28px',
												py: 0,
												px: 2,
											}}
										>
											{section.title}
										</ListSubheader>
									) : undefined
								}
							>
								{section.items.map((item, index) => {
									const path = item.path ? getMenuPath(item.path) : undefined;

									return (
										<MenuListItem
											key={index}
											open={menuOpen}
											icon={item.icon}
											primaryText={item.title}
											to={path}
											selected={isRouteSelected(location.pathname, path)}
											onClick={() => {
												if (isMobile) setIsMobileOpen(false);
												if (item.action === 'architect') {
													setIsHovering(false);
													onOpenArchitect?.();
												} else {
													item.onClick?.();
												}
											}}
										/>
									);
								})}
							</List>
						</React.Fragment>
					))}
				</Box>

				<Box component="footer" sx={{ flexShrink: 0 }}>
					{/* Auto open checkbox */}
					{showHoverToggle && !open && (
						<>
							{!menuOpen && <Divider sx={{ mx: 1, opacity: 0.5 }} />}
							<List>
								<ListItem sx={{ justifyContent: 'center', py: 0.5 }}>
									<Tooltip title="Enable hover to open" placement="right">
										<Checkbox checked={hoverEnabled} onChange={handleHoverToggle} size="small" sx={{ p: 0.5 }} />
									</Tooltip>
								</ListItem>
							</List>
						</>
					)}

					{/* ChangeSet Selector */}
					{!permissionsLoading && hasPermission(Permissions.ChangeSetRead) && (
						<>
							<List sx={{ px: menuOpen ? 2 : 1, py: 1 }}>
								<MenuItemChangeSetSelector isDrawerOpen={menuOpen} />
							</List>
						</>
					)}

					{/* User Avatar at the bottom */}
					<List>
						<MenuListItemAvatar open={menuOpen} additionalSx={{ pb: 1 }} />
					</List>
				</Box>
			</Box>
		</>
	);

	if (isMobile) {
		return (
			<>
				{!isMobileOpen && (
					<IconButton
						aria-label="Open navigation menu"
						onClick={handleDrawerToggle}
						sx={{ position: 'fixed', top: 8, left: 8, zIndex: (theme) => theme.zIndex.appBar, backgroundColor: 'background.paper', boxShadow: 1 }}
					>
						<MenuIcon />
					</IconButton>
				)}
				<Drawer
					variant="temporary"
					open={isMobileOpen}
					onClose={() => setIsMobileOpen(false)}
					ModalProps={{ keepMounted: true }}
					PaperProps={{
						sx: {
							width: DRAWER_WIDTH,
							backgroundColor: (theme) => theme.palette.navigation.background,
							borderRight: '1px solid',
							borderColor: 'divider',
						},
					}}
				>
					{menuContent(true, false)}
				</Drawer>
			</>
		);
	}

	return (
		<StyledDrawer variant="permanent" open={isDrawerOpen} isOverlay={isOverlayMode} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
			{menuContent(isDrawerOpen, true)}
		</StyledDrawer>
	);
}
